import { SECTION_KEYS } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { QueryObserverResult } from '@tanstack/react-query';
import { CommonError, GetApPayeeInfoResponse } from 'ap-openapi';
import { AxiosResponse } from 'axios';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

export const usePayeeDetailDrawer = () => {
  const context = useContext(PayeeDetailDrawerContext);
  if (!context) {
    throw new Error(
      'usePayeeDetailDrawer must be used within PayeeDetailDrawerProvider'
    );
  }
  return context;
};

type PayeeDetailDrawerContextValue = {
  refetch: () => Promise<
    QueryObserverResult<
      AxiosResponse<GetApPayeeInfoResponse, unknown>,
      CommonError
    >
  >;
  isEditingSection: SECTION_KEYS;
  setIsEditingSection: (section: SECTION_KEYS) => void;
  onClose?: (isCloseWithDirty?: boolean) => void;
  onEditFormDirtyChange?: (isEditFormDirty: boolean) => void;
  handleCancelEdit?: (isFormDirty: boolean) => Promise<void>;
};

export const PayeeDetailDrawerContext =
  createContext<PayeeDetailDrawerContextValue | null>(null);

export type ProviderProps = {
  children: ReactNode;
  refetch: () => Promise<
    QueryObserverResult<
      AxiosResponse<GetApPayeeInfoResponse, unknown>,
      CommonError
    >
  >;
  onClose?: (isCloseWithDirty?: boolean) => void;
  onEditingChange?: (isEditing: boolean) => void;
  onEditFormDirtyChange?: (isEditFormDirty: boolean) => void;
  onConfirmCancelEdit?: () => Promise<boolean>;
};

export const PayeeDetailDrawerProvider: FC<ProviderProps> = ({
  children,
  refetch,
  onClose,
  onEditingChange,
  onEditFormDirtyChange,
  onConfirmCancelEdit,
}) => {
  const [isEditingSection, setIsEditingSection] = useState<SECTION_KEYS>(
    SECTION_KEYS.NULL
  );

  const handleSetIsEditingSection = useCallback(
    (section: SECTION_KEYS) => {
      setIsEditingSection(section);
      onEditingChange?.(section !== SECTION_KEYS.NULL);
    },
    [onEditingChange]
  );

  const handleCancelEdit = useCallback(
    async (isFormDirty: boolean) => {
      if (!isFormDirty) {
        handleSetIsEditingSection(SECTION_KEYS.NULL);
        return;
      }
      if (!onConfirmCancelEdit) {
        handleSetIsEditingSection(SECTION_KEYS.NULL);
        return;
      }
      const isConfirmed = await onConfirmCancelEdit();
      if (isConfirmed) {
        handleSetIsEditingSection(SECTION_KEYS.NULL);
        onEditFormDirtyChange?.(false);
      }
    },
    [onConfirmCancelEdit, handleSetIsEditingSection, onEditFormDirtyChange]
  );

  const value: PayeeDetailDrawerContextValue = {
    refetch,
    isEditingSection,
    setIsEditingSection: handleSetIsEditingSection,
    onClose,
    onEditFormDirtyChange,
    handleCancelEdit,
  };

  return (
    <PayeeDetailDrawerContext.Provider value={value}>
      {children}
    </PayeeDetailDrawerContext.Provider>
  );
};

PayeeDetailDrawerProvider.displayName = 'PayeeDetailDrawerProvider';
